/**
 * Structured Data Component
 * Provides JSON-LD structured data for:
 * - LocalBusiness Schema
 * - MedicalOrganization Schema
 * - Service Schema (for each department)
 * - WebSite Schema with SearchAction
 */

const BASE_URL = "https://bellaroseclinics.ae";
const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/bella-rose-logo_5a3f8c12.png";

// LocalBusiness + MedicalOrganization combined schema
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["MedicalOrganization", "LocalBusiness", "Dentist"],
  "@id": `${BASE_URL}/#organization`,
  name: "Bella Rose Medical Center",
  alternateName: "مركز بيلا روز الطبي",
  url: BASE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  description: "Dubai's premier medical center specializing in dental treatments, Hollywood smile, dermatology, hair transplant (DHI & FUE), body contouring, and plastic surgery. 9 specialist doctors, open daily.",
  telephone: "+971-4-296-4442",
  email: "info@bellaroseclinics.ae",
  foundingDate: "2018",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    minValue: 20,
    maxValue: 50,
  },
  priceRange: "$$",
  currenciesAccepted: "AED",
  paymentAccepted: "Cash, Credit Card, Debit Card, Tabby, Tamara",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Muhammad Bin Rashid Charity Association Building, Cairo St, Al Mamzar",
    addressLocality: "Dubai",
    addressRegion: "Dubai",
    postalCode: "00000",
    addressCountry: "AE",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 25.2949675,
    longitude: 55.3511937,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "10:00",
      closes: "23:00",
    },
  ],
  sameAs: [
    "https://www.instagram.com/bella.rose.medical.center/",
    "https://www.facebook.com/bellarosemc.ae",
    "https://www.tiktok.com/@bellarose_medical_center",
    "https://www.snapchat.com/add/bellarosemc.ae0",
  ],
  hasMap: "https://maps.app.goo.gl/WLR3tdjmDChx5coh8",
  areaServed: {
    "@type": "City",
    name: "Dubai",
    "@id": "https://www.wikidata.org/wiki/Q612",
  },
  medicalSpecialty: [
    "Dentistry",
    "Dermatology",
    "PlasticSurgery",
    "CosmeticSurgery",
  ],
  availableService: [
    { "@type": "MedicalProcedure", name: "Dental Implants", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Hollywood Smile Veneers", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Botox Injections", procedureType: "https://schema.org/NoninvasiveProcedure" },
    { "@type": "MedicalProcedure", name: "Dermal Fillers", procedureType: "https://schema.org/NoninvasiveProcedure" },
    { "@type": "MedicalProcedure", name: "DHI Hair Transplant", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "FUE Hair Transplant", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Liposuction", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Rhinoplasty", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "CoolSculpting Fat Freezing", procedureType: "https://schema.org/NoninvasiveProcedure" },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.0",
    reviewCount: "1176",
    bestRating: "5",
    worstRating: "1",
  },
};

// WebSite schema with SearchAction for sitelinks search box
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  name: "Bella Rose Medical Center",
  alternateName: "مركز بيلا روز الطبي",
  url: BASE_URL,
  inLanguage: ["en", "ar"],
  publisher: { "@id": `${BASE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${BASE_URL}/faq?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// Service schemas for each department
const serviceSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Dental Treatments",
    alternateName: "علاجات الأسنان",
    description: "Comprehensive dental care including teeth cleaning, fillings, root canal, wisdom tooth extraction, braces, dental implants, teeth whitening, and veneers at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#dental`,
    provider: { "@id": `${BASE_URL}/#organization` },
    howPerformed: "Performed by specialist dentists using latest technology and equipment.",
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Mouth, Teeth, Gums",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Hollywood Smile",
    alternateName: "ابتسامة هوليوود",
    description: "Premium Hollywood smile makeover with porcelain veneers, lumineers, digital smile design, and zircon crowns. Transform your smile at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#hollywood-smile`,
    provider: { "@id": `${BASE_URL}/#organization` },
    howPerformed: "Custom-designed porcelain veneers crafted in our in-house lab and bonded to natural teeth.",
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Teeth",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Dermatology & Aesthetics",
    alternateName: "الجلدية والتجميل",
    description: "Advanced dermatology treatments including Botox, fillers, mesotherapy, PRP, hydrafacial, laser skin rejuvenation, and laser hair removal at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#derma`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/NoninvasiveProcedure",
    bodyLocation: "Face, Skin",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "DHI Hair Transplant",
    alternateName: "زراعة الشعر DHI",
    description: "Advanced DHI (Direct Hair Implantation) hair transplant without shaving. Natural results with minimal downtime at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#dhi-hair-transplant`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Scalp, Head",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "FUE Hair Transplant",
    alternateName: "زراعة الشعر FUE",
    description: "Classic FUE (Follicular Unit Extraction) hair transplant for scalp, beard, and eyebrows. Permanent natural results at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#fue-hair-transplant`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Scalp, Beard, Eyebrows",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Fat Dissolving & Fat Freezing",
    alternateName: "إذابة وتجميد الدهون",
    description: "Non-surgical fat reduction with CoolSculpting, fat dissolving injections, and body contouring at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#fat-dissolving`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/NoninvasiveProcedure",
    bodyLocation: "Abdomen, Thighs, Arms, Chin",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Weight Loss Machines",
    alternateName: "أجهزة إنقاص الوزن",
    description: "Advanced weight loss and body sculpting machines including cavitation, RF skin tightening, laser lipo, and EMS at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#weight-loss`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/NoninvasiveProcedure",
    bodyLocation: "Body",
  },
  {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Plastic Surgery",
    alternateName: "الجراحة التجميلية",
    description: "Expert plastic and cosmetic surgery including liposuction, tummy tuck, rhinoplasty, and more at Bella Rose Medical Center Dubai.",
    url: `${BASE_URL}/services#plastic-surgery`,
    provider: { "@id": `${BASE_URL}/#organization` },
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Body, Face",
  },
];

export function StructuredData() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      {serviceSchemas.map((schema, i) => (
        <script
          key={`svc-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
